import { useState, useEffect, useRef, useCallback } from "react";
import { tomtomApi } from "./services/tomtomApi";

const BASE = "https://support.gs-group.fr/api";
const h = () => ({ Authorization: "Bearer " + localStorage.getItem("nexo_token") });

// CARTO a fermé ses tuiles anonymes gratuites (cartocdn.com exige désormais une clé API,
// sans quoi chaque tuile affiche un watermark "API KEY REQUIRED"). Basculé sur les fonds
// de carte publics Esri (aucune clé requise).
const TILES = {
  dark:  "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
  light: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
};
const ATTR = '© <a href="https://www.esri.com/">Esri</a>';

const CSS_ANIM = `
  .dbmap-widget .leaflet-popup-content-wrapper { border-radius:12px !important; box-shadow:0 8px 28px rgba(0,0,0,.2) !important; }
  .dbmap-widget .leaflet-popup-tip { display:none !important; }
  .dbmap-widget .leaflet-control-zoom { border:none !important; box-shadow:0 2px 10px rgba(0,0,0,.15) !important; }
`;

const STAT_COLOR = { planifiee:"#f59e0b", pending:"#f59e0b", en_cours:"#6366f1", "in-progress":"#6366f1" };

const ageMin = ts => ts ? Math.round((Date.now() - new Date(ts).getTime()) / 60000) : null;
const isActive = ts => { const a = ageMin(ts); return a !== null && a < 5; };

const mkTechIcon = (L, color, active) => L.divIcon({
  html:`<div style="position:relative">
    ${active ? `<div style="position:absolute;inset:-5px;border-radius:50%;border:2px solid ${color};opacity:.5"></div>` : ""}
    <div style="width:16px;height:16px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>
  </div>`,
  className:"", iconSize:[16,16], iconAnchor:[8,8], popupAnchor:[0,-10],
});

const mkIntIcon = (L, color) => L.divIcon({
  html:`<div style="width:13px;height:13px;border-radius:3px;background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);transform:rotate(45deg)"></div>`,
  className:"", iconSize:[13,13], iconAnchor:[6,6], popupAnchor:[0,-8],
});

export default function DashboardMapWidget({ T, interventions=[], techniciens=[], onExpand, height=380 }) {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const tileRef = useRef(null);
  const trafficTileRef = useRef(null);
  const lgTech = useRef(null);
  const lgInt = useRef(null);
  const [ready, setReady] = useState(false);
  const [techPos, setTechPos] = useState([]);
  const [trafficAvailable, setTrafficAvailable] = useState(false);
  const [trafficOn, setTrafficOn] = useState(true);

  const loadTechPos = useCallback(async () => {
    try {
      const d = await fetch(`${BASE}/interventions/tech-positions`, { headers: h() }).then(r => r.json());
      if (Array.isArray(d)) setTechPos(d);
    } catch {}
  }, []);

  useEffect(() => {
    loadTechPos();
    const t = setInterval(loadTechPos, 20000);
    return () => clearInterval(t);
  }, [loadTechPos]);

  // ── Trafic temps réel TomTom (disponibilité, cf. Réglages > API & Intégrations) ──
  useEffect(() => {
    tomtomApi.isAvailable().then(setTrafficAvailable).catch(() => {});
  }, []);

  // ── Init Leaflet (réutilise window.L s'il est déjà chargé par une autre vue carte) ──
  useEffect(() => {
    if (mapInst.current || !mapRef.current) return;
    const init = () => {
      const L = window.L;
      if (!L || !mapRef.current || mapInst.current) return;
      if (!document.getElementById("dbmap-anim-css")) {
        const s = document.createElement("style"); s.id = "dbmap-anim-css"; s.textContent = CSS_ANIM;
        document.head.appendChild(s);
      }
      const map = L.map(mapRef.current, { zoomControl:false, attributionControl:false });
      map.setView([47.75, 7.35], 9);
      L.control.zoom({ position:"topright" }).addTo(map);
      lgTech.current = L.layerGroup().addTo(map);
      lgInt.current = L.layerGroup().addTo(map);
      mapInst.current = map;
      setReady(true);
    };
    if (!document.querySelector('link[href*="leaflet"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet"; l.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"; document.head.appendChild(l);
    }
    if (window.L) { setTimeout(init, 50); return; }
    if (!document.querySelector('script[src*="leaflet"]')) {
      const s = document.createElement("script"); s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"; s.onload = () => setTimeout(init, 100); document.head.appendChild(s);
    } else {
      const t = setInterval(() => { if (window.L) { clearInterval(t); init(); } }, 150);
      return () => clearInterval(t);
    }
  }, []);

  // ── Fond de carte selon le thème ──
  useEffect(() => {
    if (!ready || !window.L || !mapInst.current) return;
    const L = window.L;
    if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
    tileRef.current = L.tileLayer(TILES[T.dark ? "dark" : "light"], { attribution:ATTR, maxZoom:19 });
    tileRef.current.addTo(mapInst.current);
    tileRef.current.setZIndex(0);
  }, [ready, T.dark]);

  // ── Calque trafic temps réel (TomTom) ──
  useEffect(() => {
    if (!ready || !window.L || !mapInst.current) return;
    const L = window.L;
    if (trafficTileRef.current) { mapInst.current.removeLayer(trafficTileRef.current); trafficTileRef.current = null; }
    if (trafficOn && trafficAvailable) {
      trafficTileRef.current = L.tileLayer(tomtomApi.tileUrlTemplate(), { maxZoom:19, opacity:0.65 });
      trafficTileRef.current.addTo(mapInst.current);
      trafficTileRef.current.setZIndex(2);
    }
  }, [ready, trafficOn, trafficAvailable]);

  // ── Marqueurs techniciens ──
  useEffect(() => {
    if (!ready || !window.L || !lgTech.current) return;
    const L = window.L;
    lgTech.current.clearLayers();
    const pts = [];
    techPos.forEach(tp => {
      if (!tp.lat || !tp.lng) return;
      const active = isActive(tp.updated_at);
      const color = active ? "#22c55e" : "#94a3b8";
      const marker = L.marker([tp.lat, tp.lng], { icon: mkTechIcon(L, color, active) });
      marker.bindPopup(`<div style="font-family:system-ui,sans-serif;font-size:12px;min-width:150px">
        <strong style="font-size:13px">${tp.prenom||""} ${tp.nom||""}</strong><br/>
        ${tp.inter_titre ? `🔧 ${tp.inter_titre}${tp.client_nom?` · ${tp.client_nom}`:""}` : `<span style="color:#16a34a">✅ Disponible</span>`}
      </div>`);
      lgTech.current.addLayer(marker);
      pts.push([tp.lat, tp.lng]);
    });
    if (pts.length && !mapInst.current._dbmapFitted) {
      mapInst.current.fitBounds(pts, { padding:[30,30], maxZoom:12 });
      mapInst.current._dbmapFitted = true;
    }
  }, [ready, techPos]);

  // ── Marqueurs interventions actives / planifiées du jour ──
  useEffect(() => {
    if (!ready || !window.L || !lgInt.current) return;
    const L = window.L;
    lgInt.current.clearLayers();
    interventions
      .filter(i => ["planifiee","pending","en_cours","in-progress"].includes(i.statut))
      .forEach(i => {
        const lat = i.client_lat || i.lat, lng = i.client_lng || i.lng;
        if (!lat || !lng) return;
        const color = STAT_COLOR[i.statut] || "#8899bb";
        const marker = L.marker([lat, lng], { icon: mkIntIcon(L, color) });
        marker.bindPopup(`<div style="font-family:system-ui,sans-serif;font-size:12px;min-width:160px">
          <strong style="font-size:13px">${i.titre||"—"}</strong><br/>
          ${i.client_nom||"—"}<br/>
          <span style="color:${color};font-weight:700">${i.technicien_nom||"Non assigné"}</span>
        </div>`);
        lgInt.current.addLayer(marker);
      });
  }, [ready, interventions]);

  const activeCount = techPos.filter(tp => isActive(tp.updated_at)).length;

  return (
    <div className="dbmap-widget" style={{ position:"relative", height, borderRadius:14, overflow:"hidden", border:`1px solid ${T.border}` }}>
      <div ref={mapRef} style={{ width:"100%", height:"100%", background:T.bg }} />
      <div style={{ position:"absolute", top:12, left:12, zIndex:1000, display:"flex", alignItems:"center", gap:6, background:T.dark?"rgba(24,24,27,0.85)":"rgba(255,255,255,0.9)", backdropFilter:"blur(8px)", padding:"6px 12px", borderRadius:99, border:`1px solid ${T.border}`, fontSize:11, fontWeight:700, color:T.text }}>
        <span style={{ width:7, height:7, borderRadius:"50%", background:activeCount>0?"#22c55e":"#94a3b8" }}/>
        {activeCount} technicien{activeCount>1?"s":""} actif{activeCount>1?"s":""}
      </div>
      {trafficAvailable && (
        <button onClick={() => setTrafficOn(v => !v)} style={{ position:"absolute", top:12, right:onExpand?142:52, zIndex:1000, padding:"6px 12px", borderRadius:99, background:trafficOn?"#f59e0b":(T.dark?"rgba(24,24,27,0.85)":"rgba(255,255,255,0.9)"), backdropFilter:"blur(8px)", border:`1px solid ${trafficOn?"#f59e0b":T.border}`, color:trafficOn?"#fff":T.text, fontSize:11, fontWeight:700, cursor:"pointer" }}>
          🚦 Trafic
        </button>
      )}
      {onExpand && (
        <button onClick={onExpand} style={{ position:"absolute", top:12, right:52, zIndex:1000, padding:"6px 12px", borderRadius:99, background:T.dark?"rgba(24,24,27,0.85)":"rgba(255,255,255,0.9)", backdropFilter:"blur(8px)", border:`1px solid ${T.border}`, color:T.text, fontSize:11, fontWeight:700, cursor:"pointer" }}>
          ⤢ Plein écran
        </button>
      )}
      {!ready && (
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:T.bg, fontSize:12, color:T.muted }}>
          Chargement de la carte…
        </div>
      )}
    </div>
  );
}
